import {
  useMutation,
  useQueryClient,
  useQuery,
  UseMutationResult,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import { DateTime } from 'luxon';
import {
  BookingResource,
  ResourceBooking,
  UserBooking,
} from '../types/booking';
import { useAuth } from './useAuth';
import { useApiClient } from './useApiClient';
import {
  ApiIloqBooking,
  ApiIloqCreateBookingRequest,
} from '@allbin/viu-api-client';
import { useIdentity } from '../providers/IdentityProvider';
import { useCallback } from 'react';
import useConfig from './useConfig';

interface CreateBookingParams {
  deviceId: string;
  resourceId: string;
  startTime: string;
  endTime: string;
  keyNfcId: string;
  subject?: string;
}

interface CancelBookingParams {
  deviceId: string;
  bookingId: string;
  resourceId: string;
  keyNfcId: string;
}

// Shared by the query and the mutations' optimistic cache updates: an exact
// getQueryData/setQueryData only hits when every part of the key matches.
const userBookingsQueryKey = (
  deviceId: string | undefined,
  nfcKeyId: string | undefined,
  bookingSystem: string | undefined,
): QueryKey => ['userBookings', deviceId, nfcKeyId, bookingSystem];

const resourceBookingsQueryKey = (
  deviceId: string | undefined,
  resourceId: string,
  bookingSystem: string | undefined,
): QueryKey => ['resourceBookings', deviceId, resourceId, bookingSystem];

export const useUserBookings = (
  resources?: BookingResource[],
): UseQueryResult<UserBooking[]> => {
  const config = useConfig();
  const { nfcKeyId } = useAuth();
  const apiClient = useApiClient();
  const { id: deviceId } = useIdentity(
    useCallback((state) => ({ id: state.id }), []),
  );

  return useQuery({
    queryKey: userBookingsQueryKey(deviceId, nfcKeyId, config.bookingSystem),
    queryFn: async (): Promise<UserBooking[]> => {
      if (!deviceId || !nfcKeyId) {
        console.warn(
          '[useUserBookings] deviceId or nfcKeyId missing, skipping fetch.',
        );
        return [];
      }

      if (config.bookingSystem !== 'iloq') {
        console.warn(
          '[useUserBookings] Booking system is not iloq, skipping fetch.',
        );
        return [];
      }

      try {
        const responseData = await apiClient.public.devices.getIloqUserBookings(
          deviceId,
          nfcKeyId,
        );

        const bookings = responseData.bookings || [];

        const userBookings: UserBooking[] = [];

        bookings
          .sort((a, b) => {
            return DateTime.fromISO(a.start_time, { zone: 'UTC' })
              .diff(DateTime.fromISO(b.start_time, { zone: 'UTC' }))
              .toMillis();
          })
          .forEach((booking: ApiIloqBooking) => {
            if (!booking.resource_id) {
              console.warn('[useUserBookings] Booking is missing resourceId');
              return;
            }

            const resource = resources?.find(
              (r) => r.id === booking.resource_id,
            );
            if (!resource) {
              console.warn(
                `[useUserBookings] Could not find resource mapping for id: ${booking.resource_id}`,
              );
            }

            const start = DateTime.fromISO(booking.start_time, {
              zone: 'UTC',
            }).toLocal();
            const startString = start.toISO();

            const end = DateTime.fromISO(booking.end_time, {
              zone: 'UTC',
            }).toLocal();
            const endString = end.toISO();

            if (!startString || !endString) {
              console.warn(
                '[useUserBookings] Invalid booking times for booking:',
                booking.booking_id,
              );
              return;
            }

            const isPast = end.diffNow().milliseconds < 0;
            if (isPast) {
              return;
            }

            userBookings.push({
              id: booking.booking_id,
              start: startString,
              end: endString,
              isAvailable: false,
              status: 'userBooked',
              bookingId: booking.booking_id,
              bookedBy: booking.key_nfc_id || nfcKeyId,
              resourceId: booking.resource_id,
              subject: booking.name ?? undefined,
            });
          });

        return userBookings;
      } catch (error) {
        console.error('[useUserBookings] Failed to fetch:', error);
        throw error;
      }
    },
    enabled: !!deviceId && !!nfcKeyId && !!resources?.length,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useResourceBookings = (
  resourceId: string,
): UseQueryResult<ResourceBooking[]> => {
  const config = useConfig();
  const apiClient = useApiClient();
  const { id: deviceId } = useIdentity(
    useCallback((state) => ({ id: state.id }), []),
  );

  return useQuery({
    queryKey: resourceBookingsQueryKey(
      deviceId,
      resourceId,
      config.bookingSystem,
    ),
    queryFn: async (): Promise<ResourceBooking[]> => {
      if (!deviceId) {
        console.warn('[useResourceBookings] deviceId missing, skipping fetch.');
        return [];
      }

      if (config.bookingSystem !== 'iloq') {
        console.warn(
          '[useResourceBookings] Booking system is not iloq, skipping fetch.',
        );
        return [];
      }

      try {
        const responseData =
          await apiClient.public.devices.getIloqResourceBookings(
            deviceId,
            resourceId,
          );

        const bookings = responseData.bookings || [];

        // Mappa och filtrera i separata steg för tydligare typer
        const mappedBookings = bookings.map((booking: ApiIloqBooking) => {
          const start = DateTime.fromISO(booking.start_time, { zone: 'UTC' })
            .toLocal()
            .toISO();
          const end = DateTime.fromISO(booking.end_time, { zone: 'UTC' })
            .toLocal()
            .toISO();

          if (!start || !end) {
            console.warn(
              '[useResourceBookings] Invalid booking times for booking:',
              booking.booking_id,
            );
            return null;
          }

          // Skapa ett objekt med alla fält, inklusive de som ärver från BookingSlot
          const resourceBookingCandidate: ResourceBooking = {
            id: booking.booking_id,
            start,
            end,
            isAvailable: false,
            subject: booking.name ?? undefined, // Mappa name till subject
            resourceId: booking.resource_id,
            bookedBy: booking.key_nfc_id,
            // type och isRecurring saknas i ApiIloqBooking och utelämnas
            // status behövs inte i ResourceBooking? Lägg till om det krävs.
          };
          return resourceBookingCandidate;
        });

        // Filtrera bort null och säkerställ att det är ResourceBooking
        const validBookings: ResourceBooking[] = mappedBookings.filter(
          (b): b is ResourceBooking => b !== null,
        );

        return validBookings;
      } catch (error) {
        console.error('[useResourceBookings] Failed to fetch:', error);
        throw error;
      }
    },
    enabled: !!deviceId && !!resourceId,
    refetchInterval: 30 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

interface UseBookingMutations {
  create: UseMutationResult<
    ApiIloqBooking,
    Error,
    CreateBookingParams,
    {
      previousResourceBookingsQueries?: [
        QueryKey,
        ResourceBooking[] | undefined,
      ][];
      previousUserBookings?: UserBooking[];
    }
  >;
  cancel: UseMutationResult<
    void,
    Error,
    CancelBookingParams,
    {
      previousResourceBookingsQueries?: [
        QueryKey,
        ResourceBooking[] | undefined,
      ][];
      previousUserBookings?: UserBooking[];
    }
  >;
}
export const useBookingMutations = (): UseBookingMutations => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const config = useConfig();

  const create = useMutation<
    ApiIloqBooking,
    Error,
    CreateBookingParams,
    {
      previousResourceBookingsQueries?: [
        QueryKey,
        ResourceBooking[] | undefined,
      ][];
      previousUserBookings?: UserBooking[];
    }
  >({
    mutationFn: async (
      params: CreateBookingParams,
    ): Promise<ApiIloqBooking> => {
      const bookingData: ApiIloqCreateBookingRequest = {
        resource_id: params.resourceId,
        key_nfc_id: params.keyNfcId,
        start_time: params.startTime,
        end_time: params.endTime,
        subject: params.subject,
      };

      try {
        const createdBooking = await apiClient.public.devices.createIloqBooking(
          params.deviceId,
          bookingData,
        );

        return createdBooking;
      } catch (error) {
        console.error('[createBooking mutation] Fel:', error);
        throw error;
      }
    },
    onMutate: async (newBookingParams) => {
      const resourceQueryKey = resourceBookingsQueryKey(
        newBookingParams.deviceId,
        newBookingParams.resourceId,
        config.bookingSystem,
      );
      const userQueryKey = userBookingsQueryKey(
        newBookingParams.deviceId,
        newBookingParams.keyNfcId,
        config.bookingSystem,
      );

      await queryClient.cancelQueries({
        queryKey: resourceQueryKey,
        exact: false,
      });
      await queryClient.cancelQueries({ queryKey: userQueryKey });

      const previousResourceBookingsQueries = queryClient.getQueriesData<
        ResourceBooking[]
      >({ queryKey: resourceQueryKey, exact: false });
      const previousUserBookings =
        queryClient.getQueryData<UserBooking[]>(userQueryKey);

      const optimisticBooking: UserBooking & ResourceBooking = {
        id: `optimistic-${Date.now()}`,
        start: newBookingParams.startTime,
        end: newBookingParams.endTime,
        isAvailable: false,
        status: 'userBooked',
        bookingId: `optimistic-${Date.now()}`,
        bookedBy: newBookingParams.keyNfcId,
        resourceId: newBookingParams.resourceId,
        subject: newBookingParams.subject,
      };

      queryClient.setQueriesData<ResourceBooking[]>(
        { queryKey: resourceQueryKey, exact: false },
        (old) => [...(old || []), optimisticBooking],
      );

      if (previousUserBookings) {
        queryClient.setQueryData<UserBooking[]>(userQueryKey, [
          ...previousUserBookings,
          optimisticBooking,
        ]);
      }

      return { previousResourceBookingsQueries, previousUserBookings };
    },
    onError: (err, newBookingParams, context) => {
      const userQueryKey = userBookingsQueryKey(
        newBookingParams.deviceId,
        newBookingParams.keyNfcId,
        config.bookingSystem,
      );

      if (context?.previousResourceBookingsQueries) {
        context.previousResourceBookingsQueries.forEach(
          ([queryKey, data]: [QueryKey, ResourceBooking[] | undefined]) => {
            queryClient.setQueryData(queryKey, data);
          },
        );
      }
      if (context?.previousUserBookings) {
        queryClient.setQueryData(userQueryKey, context.previousUserBookings);
      }
      console.error('Optimistic create failed, rolled back:', err);
    },
    onSettled: (data, error, variables) => {
      const resourceQueryKey = resourceBookingsQueryKey(
        variables.deviceId,
        variables.resourceId,
        config.bookingSystem,
      );
      const userQueryKey = userBookingsQueryKey(
        variables.deviceId,
        variables.keyNfcId,
        config.bookingSystem,
      );

      void queryClient.invalidateQueries({
        queryKey: resourceQueryKey,
        exact: false,
      });
      void queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });

  const cancel = useMutation<
    void,
    Error,
    CancelBookingParams,
    {
      previousResourceBookingsQueries?: [
        QueryKey,
        ResourceBooking[] | undefined,
      ][];
      previousUserBookings?: UserBooking[];
    }
  >({
    mutationFn: async ({
      deviceId,
      bookingId,
      keyNfcId,
    }: CancelBookingParams): Promise<void> => {
      try {
        await apiClient.public.devices.deleteIloqBooking(
          deviceId,
          bookingId,
          keyNfcId,
        );
      } catch (error) {
        console.error('[cancelBooking mutation] Fel:', error);
        throw error;
      }
    },
    onMutate: async (params) => {
      const resourceQueryKey = resourceBookingsQueryKey(
        params.deviceId,
        params.resourceId,
        config.bookingSystem,
      );
      const userQueryKey = userBookingsQueryKey(
        params.deviceId,
        params.keyNfcId,
        config.bookingSystem,
      );

      await queryClient.cancelQueries({
        queryKey: resourceQueryKey,
        exact: false,
      });
      await queryClient.cancelQueries({ queryKey: userQueryKey });

      const previousResourceBookingsQueries = queryClient.getQueriesData<
        ResourceBooking[]
      >({ queryKey: resourceQueryKey, exact: false });
      const previousUserBookings =
        queryClient.getQueryData<UserBooking[]>(userQueryKey);

      queryClient.setQueriesData<ResourceBooking[]>(
        { queryKey: resourceQueryKey, exact: false },
        (old) =>
          (old || []).filter((booking) => booking.id !== params.bookingId),
      );
      if (previousUserBookings) {
        queryClient.setQueryData<UserBooking[]>(
          userQueryKey,
          previousUserBookings.filter(
            (booking) => booking.id !== params.bookingId,
          ),
        );
      }

      return { previousResourceBookingsQueries, previousUserBookings };
    },
    onError: (err, params, context) => {
      const userQueryKey = userBookingsQueryKey(
        params.deviceId,
        params.keyNfcId,
        config.bookingSystem,
      );
      if (context?.previousResourceBookingsQueries) {
        context.previousResourceBookingsQueries.forEach(
          ([queryKey, data]: [QueryKey, ResourceBooking[] | undefined]) => {
            queryClient.setQueryData(queryKey, data);
          },
        );
      }
      if (context?.previousUserBookings) {
        queryClient.setQueryData(userQueryKey, context.previousUserBookings);
      }
      console.error('Optimistic cancel failed, rolled back:', err);
    },
    onSettled: (data, error, variables) => {
      const resourceQueryKey = resourceBookingsQueryKey(
        variables.deviceId,
        variables.resourceId,
        config.bookingSystem,
      );
      const userQueryKey = userBookingsQueryKey(
        variables.deviceId,
        variables.keyNfcId,
        config.bookingSystem,
      );

      void queryClient.invalidateQueries({
        queryKey: resourceQueryKey,
        exact: false,
      });
      void queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });

  return { create, cancel };
};
